"use client"

const LISTING_ATTRIBUTION_STORAGE_KEY = "ga4_listing_attribution"
const LISTING_ATTRIBUTION_TTL_MS = 2 * 60 * 1000

export interface ListingAttribution {
  productId: string
  item_list_id: string
  item_list_name: string
  index?: number
  createdAt: number
}

export function buildCategoryListingAttribution(categoryPath: string[], productId: string, index?: number) {
  const topCats = categoryPath.filter(Boolean)
  if (topCats.length === 0) return undefined

  const depth = topCats.length
  const categoryLevel = depth >= 4 ? "l4" : depth === 3 ? "l3" : depth === 2 ? "l2" : "l1"
  const listIdBase = (topCats.at(-1) || "list")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

  return {
    productId,
    item_list_id: `cat_${categoryLevel}_${listIdBase}`,
    item_list_name: topCats.at(-1) || "",
    index,
    createdAt: Date.now(),
  }
}

export function storeListingAttribution(attribution: ListingAttribution | undefined) {
  if (!attribution || typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(LISTING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution))
  } catch {
    // Attribution is optional. If storage is unavailable, view_item simply omits the list context.
  }
}

export function clearListingAttribution() {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.removeItem(LISTING_ATTRIBUTION_STORAGE_KEY)
  } catch {
    // Optional tracking context only.
  }
}

export function consumeListingAttributionForProduct(productIds: Array<string | undefined>) {
  if (typeof window === "undefined") return undefined

  try {
    const rawAttribution = window.sessionStorage.getItem(LISTING_ATTRIBUTION_STORAGE_KEY)
    if (!rawAttribution) return undefined

    const attribution = JSON.parse(rawAttribution) as ListingAttribution
    const allowedProductIds = productIds.filter(Boolean)
    const isExpired = Date.now() - attribution.createdAt > LISTING_ATTRIBUTION_TTL_MS
    const matchesProduct = allowedProductIds.includes(attribution.productId)

    window.sessionStorage.removeItem(LISTING_ATTRIBUTION_STORAGE_KEY)

    if (isExpired || !matchesProduct) {
      return undefined
    }

    return attribution
  } catch {
    clearListingAttribution()
    return undefined
  }
}
